import React, { memo, useState, useEffect } from 'react';
import { useKeyboardNavigation } from '../../../components/BatchCoinForm/hooks/useKeyboardNavigation';
import { certificationServices } from '../../../utils/api';
import { ImportLogs } from './ImportLogs';

export const baseFields = [
  // Basic Information
  { key: 'lot_number', label: 'Lot #', type: 'text', required: true },
  { key: 'country', label: 'Country', type: 'text', required: true },
  { key: 'year', label: 'Year', type: 'text', required: true },
  { key: 'mint_mark', label: 'Mint Mark', type: 'text' },
  { key: 'denomination', label: 'Denomination', type: 'text', required: true },
  { key: 'variety', label: 'Variety', type: 'text' },

  // Certification Information
  { key: 'certification_service_id', label: 'TPG', type: 'select', options: [] },
  { key: 'certification_number', label: 'Cert #', type: 'text' },
  { key: 'pcgs_number', label: 'PCGS #', type: 'text' },
  { key: 'grade', label: 'Grade', type: 'text' },
  { key: 'cac_sticker', label: 'CAC', type: 'select', options: [
    { value: 'none', label: 'None' },
    { value: 'green', label: 'Green' },
    { value: 'gold', label: 'Gold' }
  ]},

  // Physical Attributes
  { key: 'metal', label: 'Metal', type: 'text' },
  { key: 'composition', label: 'Composition %', type: 'number', step: '0.1' },
  { key: 'weight', label: 'Weight (g)', type: 'number', step: '0.001' },
  { key: 'diameter', label: 'Diameter (mm)', type: 'number', step: '0.01' },
  { key: 'edge', label: 'Edge', type: 'text' },

  // Population Data
  { key: 'mintage', label: 'Mintage', type: 'text' },
  { key: 'population', label: 'Population', type: 'number' },
  { key: 'pop_higher', label: 'Pop Higher', type: 'number' },

  // Variety Information
  { key: 'assayer', label: 'Assayer', type: 'text' },
  { key: 'minor_variety', label: 'Minor Variety', type: 'text' },
  { key: 'die_variety', label: 'Die Variety', type: 'text' },
  { key: 'reference_number', label: 'Reference #', type: 'text' },

  // Price Information
  { key: 'price_guide_value', label: 'Price Guide', type: 'number', step: '0.01' },
  { key: 'estimated_retail_price', label: 'Max Price', type: 'number', step: '0.01' },

  // Additional Information
  { key: 'notes', label: 'Notes', type: 'text' }
];

export const BatchLotForm = memo(function BatchLotForm({
  rows,
  selectedCells,
  isDragging,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  onPaste,
  onCellChange,
  onKeyDown,
  onRemoveRow,
  onFetchCertDetails,
  fetchProgress,
  logs
}) {
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const [fields, setFields] = useState(baseFields);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const services = await certificationServices.getAll();
        // Create a new copy of baseFields and update the TPG field with service options
        const newFields = [...baseFields];
        const tpgFieldIndex = newFields.findIndex(field => field.key === 'certification_service_id');
        if (tpgFieldIndex !== -1) {
          newFields[tpgFieldIndex] = {
            ...newFields[tpgFieldIndex],
            options: [
              { value: '', label: 'Select TPG' },
              ...services.map(service => ({
                value: service.id.toString(),
                label: service.name
              }))
            ]
          };
        }
        setFields(newFields);
      } catch (error) {
        console.error('Failed to fetch certification services:', error);
      }
    };
    fetchServices();
  }, []); // Empty dependency array since we only need to fetch services once
  const { handleKeyDown: handleArrowKeys, activeCell } = useKeyboardNavigation({ 
    rows, 
    fields,
    onNavigationStart: () => {
      onCellMouseUp(); // Clear selected cells
      setIsKeyboardMode(true);
    },
    onKeyDown
  });

  const handleMouseDown = (e, rowIndex, field) => {
    setIsKeyboardMode(false);
    onCellMouseDown(e, rowIndex, field);
  };

  return (
    <div 
      className="border border-gray-200 rounded-lg relative" 
      onMouseLeave={() => {
        if (isDragging) {
          onCellMouseUp();
        }
      }}
    >
      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={onFetchCertDetails}
          disabled={fetchProgress.total > 0}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
        >
          {fetchProgress.total > 0 
            ? `Fetching... (${fetchProgress.current}/${fetchProgress.total})`
            : 'Fetch Cert Details'
          }
        </button>
      </div>

      <div className="overflow-x-auto" style={{ maxHeight: '60vh' }}>
        <table className="min-w-full divide-y divide-gray-200 border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th scope="col" className="w-8 px-2 py-1 sticky left-0 top-0 z-10 bg-gray-100 border border-gray-200 shadow-sm">
                </th>
                {fields.map(field => (
                  <th 
                    key={field.key}
                    scope="col" 
                    className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200 sticky top-0 z-10 bg-gray-100 shadow-sm"
                    style={{
                      minWidth: `${Math.max(field.label.length * 8, 120)}px`
                    }}
                  >
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-2 py-1 whitespace-nowrap text-sm sticky left-0 bg-inherit border border-gray-200">
                    <button
                      type="button"
                      onClick={() => onRemoveRow(rowIndex)}
                      className="text-red-600 hover:text-red-900 font-medium text-lg"
                    >
                      ×
                    </button>
                  </td>
                  {fields.map((field, fieldIndex) => (
                    <td 
                      key={field.key} 
                      className={`p-0 text-sm ${
                        !isKeyboardMode && selectedCells.some(cell => 
                          cell.rowIndex === rowIndex && cell.field === field.key
                        ) ? 'border-2 border-solid border-blue-500' : 
                        activeCell?.rowIndex === rowIndex && activeCell?.fieldIndex === fieldIndex ? 
                        'border-2 border-solid border-blue-500' : 'border border-gray-200'
                      } ${isDragging ? 'select-none' : ''}`}
                      style={{
                        userSelect: isDragging ? 'none' : 'text',
                        cursor: isDragging ? 'cell' : 'default',
                        position: 'relative',
                        minWidth: `${Math.max(field.label.length * 8, 120)}px`
                      }}
                      onMouseDown={(e) => handleMouseDown(e, rowIndex, field.key)}
                      onMouseEnter={() => onCellMouseEnter(rowIndex, field.key)}
                      onMouseUp={onCellMouseUp}
                      onPaste={(e) => {
                        onPaste(e, rowIndex, field.key);
                        e.preventDefault();
                      }}
                    >
                      {field.type === 'select' ? (
                        <select
                          value={row[field.key] === null ? '' : row[field.key] || ''}
                          onChange={(e) => onCellChange(rowIndex, field.key, e.target.value)}
                          className="block w-full h-full px-2 py-1 text-sm border-0 outline-none focus:ring-0 focus:outline-none relative"
                          data-row={rowIndex}
                          data-field={field.key}
                          onKeyDown={(e) => {
                            handleArrowKeys(e, rowIndex, fieldIndex, fields);
                            if (['Tab', 'Enter'].includes(e.key)) {
                              onKeyDown(e, rowIndex, fieldIndex, fields);
                            }
                          }}
                        >
                          {field.options.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type}
                          step={field.step}
                          value={row[field.key] === null ? '' : row[field.key] || ''}
                          onChange={(e) => onCellChange(
                            rowIndex, 
                            field.key, 
                            field.type === 'number' ? e.target.value === '' ? null : Number(e.target.value) : e.target.value
                          )}
                          className="block w-full h-full px-2 py-1 text-sm border-0 outline-none focus:ring-0 focus:outline-none relative break-words"
                          data-row={rowIndex}
                          data-field={field.key}
                          onKeyDown={(e) => {
                            handleArrowKeys(e, rowIndex, fieldIndex, fields);
                            if (['Tab', 'Enter'].includes(e.key)) {
                              onKeyDown(e, rowIndex, fieldIndex, fields);
                            }
                          }}
                          onPaste={(e) => {
                            onPaste(e, rowIndex, field.key);
                            e.preventDefault();
                          }}
                          placeholder={field.required ? 'Required' : ''}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
        </table>
      </div>
      
      {logs && logs.length > 0 && (
        <div className="mt-4">
          <ImportLogs logs={logs} />
        </div>
      )}
    </div>
  );
});
